import { tenantCalendarDayKey } from '@kit/shared/timezone';

export type ActivityKind = 'visit' | 'class' | 'training';

export interface MonthActivity {
  /** 'YYYY-MM' */
  month: string;
  /** Tenant-local 'YYYY-MM-DD'. */
  today: string;
  /** Tenant-local day key → the kinds of activity on that day. */
  days: Record<string, ActivityKind[]>;
  /** Visits are distinct check-in days; classes and training are sessions. */
  counts: Record<ActivityKind, number>;
}

export interface FamilyBookingEntry {
  booking_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  booking_status?: string | null;
  is_class_enrollment?: boolean | null;
  participant_status?: string | null;
  trainer_id?: string | null;
  is_trainer_session?: boolean | null;
}

const MONTH_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

// Staff rarely mark class attendance, so a past confirmed enrollment counts
// as taken unless it was explicitly marked no-show.
const COUNTED_PARTICIPANT_STATUSES = new Set(['confirmed', 'attended']);

const pad = (value: number) => String(value).padStart(2, '0');

export function todayInTimeZone(timeZone: string): string {
  return tenantCalendarDayKey(new Date(), timeZone);
}

/** Tenant-local wall-clock time as 'HH:mm'. */
export function nowTimeInTimeZone(timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(new Date());
}

function nextDayKey(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const next = new Date(Date.UTC(year!, month! - 1, date! + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

// A session only counts once it has ended. One that wraps past midnight
// (end <= start) ends on the day after its booking date.
function hasEndedBy(
  entry: FamilyBookingEntry,
  today: string,
  nowTime: string,
): boolean {
  const day = entry.booking_date ?? '';
  const start = entry.start_time?.slice(0, 5) ?? '';
  const end = entry.end_time?.slice(0, 5) ?? '';
  const endDay = end && end <= start ? nextDayKey(day) : day;

  if (endDay !== today) return endDay < today;
  return Boolean(end) && end <= nowTime;
}

export function parseMonthKey(
  value: string | null | undefined,
  fallback: string,
): string {
  return value && MONTH_KEY_PATTERN.test(value) ? value : fallback;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, monthIndex] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year!, monthIndex! - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

/** Sunday-first month layout: blanks before day 1 and the number of days. */
export function monthGrid(month: string) {
  const [year, monthIndex] = month.split('-').map(Number);
  return {
    leadingBlanks: new Date(Date.UTC(year!, monthIndex! - 1, 1)).getUTCDay(),
    daysInMonth: new Date(Date.UTC(year!, monthIndex!, 0)).getUTCDate(),
  };
}

export function dayKeyFor(month: string, day: number): string {
  return `${month}-${pad(day)}`;
}

export function classifyBooking(
  entry: FamilyBookingEntry,
): Exclude<ActivityKind, 'visit'> | null {
  if (entry.is_class_enrollment) {
    return entry.booking_status !== 'cancelled' &&
      COUNTED_PARTICIPANT_STATUSES.has(entry.participant_status ?? '')
      ? 'class'
      : null;
  }

  if (entry.booking_status !== 'confirmed') return null;

  return entry.is_trainer_session || entry.trainer_id ? 'training' : null;
}

export function summarizeMonthActivity(input: {
  month: string;
  today: string;
  /** Tenant-local 'HH:mm'; sessions later today are not counted yet. */
  nowTime: string;
  visitDays: readonly string[];
  bookings: readonly FamilyBookingEntry[];
}): MonthActivity {
  const { month, today } = input;
  const inMonthAndPast = (day: string) =>
    day.startsWith(`${month}-`) && day <= today;

  const kindsByDay = new Map<string, Set<ActivityKind>>();
  const counts: Record<ActivityKind, number> = {
    visit: 0,
    class: 0,
    training: 0,
  };

  const mark = (day: string, kind: ActivityKind) => {
    const kinds = kindsByDay.get(day) ?? new Set<ActivityKind>();
    kinds.add(kind);
    kindsByDay.set(day, kinds);
  };

  for (const day of new Set(input.visitDays)) {
    if (!inMonthAndPast(day)) continue;
    counts.visit += 1;
    mark(day, 'visit');
  }

  for (const entry of input.bookings) {
    const day = entry.booking_date ?? '';
    if (!inMonthAndPast(day)) continue;
    if (!hasEndedBy(entry, today, input.nowTime)) continue;

    const kind = classifyBooking(entry);
    if (!kind) continue;

    counts[kind] += 1;
    mark(day, kind);
  }

  const days: Record<string, ActivityKind[]> = {};
  for (const [day, kinds] of kindsByDay) {
    days[day] = [...kinds];
  }

  return { month, today, days, counts };
}

export const ACTIVITY_KIND_COLORS: Record<ActivityKind, string> = {
  visit: 'var(--chart-2)',
  class: 'var(--chart-1)',
  training: 'var(--chart-4)',
};
